import { ArrowRight, Trash2 } from "lucide-react";

import { SearchableSelect } from "@/components/shared/searchable-select";
import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";

import { ExpenseItemModeBar } from "./expense-item-mode-bar";
import {
  buildConversionHint,
  filterUomOptions,
  isUomCompatible,
  toBaseQty,
} from "./expense-item-utils";

import type { ExpenseFormValues } from "./expense-schema";
import type {
  Product,
  RawMaterial,
  UnitConversion,
  UnitOfMeasurement,
} from "@artisancode/api-types";
import type { UseFormReturn } from "react-hook-form";

interface Props {
  form: UseFormReturn<ExpenseFormValues>;
  index: number;
  canRemove: boolean;
  onRemove: () => void;
  rawMaterials: RawMaterial[];
  products: Product[];
  uoms: UnitOfMeasurement[];
  conversions: UnitConversion[];
}

/** One expense line: either an operational cost or a stock receipt (raw material / product). */
export function ExpenseItemRow({
  form,
  index,
  canRemove,
  onRemove,
  rawMaterials,
  products,
  uoms,
  conversions,
}: Props) {
  const uomMap = new Map(uoms.map((u) => [u.id, u]));

  const itemType = form.watch(`items.${index}.itemType`);
  const itemId = form.watch(`items.${index}.itemId`);
  const uomId = form.watch(`items.${index}.uomId`);
  const qtyInUomVal = form.watch(`items.${index}.qtyInUom`);
  const qtyVal = Number(form.watch(`items.${index}.qty`)) || 0;
  const priceVal = Number(form.watch(`items.${index}.pricePerUnit`)) || 0;

  const isStockReceipt = Boolean(itemType);
  // The Qty input shows `qtyInUom ?? 1`; use exactly that so a never-typed qty
  // isn't confused with the base-unit `qty` after a unit change.
  const purchaseQty = Number(qtyInUomVal ?? 1) || 1;
  const pinPurchaseQty = () =>
    form.setValue(`items.${index}.qtyInUom`, purchaseQty);

  // Find base UoM for selected item
  let baseUomId = "";
  let baseUomName = "";
  if (itemType === "raw_material" && itemId) {
    const rm = rawMaterials.find((r) => r.id === itemId);
    if (rm) {
      baseUomId = rm.uomId;
      baseUomName = uomMap.get(rm.uomId)?.name || "Unit";
    }
  } else if (itemType === "product" && itemId) {
    const prod = products.find((p) => p.id === itemId);
    if (prod) {
      baseUomId = prod.uomId || "";
      baseUomName = prod.uomId ? uomMap.get(prod.uomId)?.name || "Pcs" : "Pcs";
    }
  }

  // Active UoM
  const activeUomId = uomId || baseUomId;
  const activeUomName = activeUomId
    ? uomMap.get(activeUomId)?.name || baseUomName
    : baseUomName;

  const conversionText = isStockReceipt
    ? buildConversionHint({
        conversions,
        activeUomId,
        baseUomId,
        baseUomName,
        inputQty: purchaseQty,
        pricePerUnit: priceVal,
      })
    : null;

  const currentQty = isStockReceipt ? purchaseQty : qtyVal;
  const lineSubtotal = currentQty * priceVal;

  const switchType = (type: "raw_material" | "product") => {
    form.setValue(`items.${index}.itemType`, type);
    form.setValue(`items.${index}.itemId`, null);
    form.setValue(`items.${index}.uomId`, null);
  };

  const pickItem = (val: string) => {
    form.setValue(`items.${index}.itemId`, val || null);
    pinPurchaseQty();
    if (!val) {
      form.setValue(`items.${index}.uomId`, null);
      return;
    }
    if (itemType === "raw_material") {
      const rm = rawMaterials.find((r) => r.id === val);
      if (rm) {
        form.setValue(`items.${index}.description`, `Pembelian: ${rm.name}`);
        const currentUomId = form.getValues(`items.${index}.uomId`);
        const isValid =
          currentUomId && isUomCompatible(currentUomId, rm.uomId, conversions);
        const nextUomId = isValid ? currentUomId : rm.uomId;
        form.setValue(`items.${index}.uomId`, nextUomId);
        form.setValue(
          `items.${index}.qty`,
          toBaseQty(conversions, purchaseQty, nextUomId, rm.uomId, true),
        );
        if (!priceVal && rm.costPerUnit) {
          form.setValue(`items.${index}.pricePerUnit`, Number(rm.costPerUnit));
        }
      }
    } else if (itemType === "product") {
      const prod = products.find((p) => p.id === val);
      if (prod) {
        form.setValue(`items.${index}.description`, `Pembelian: ${prod.name}`);
        const targetBaseUomId = prod.uomId || "";
        const currentUomId = form.getValues(`items.${index}.uomId`);
        const isValid =
          currentUomId &&
          targetBaseUomId &&
          isUomCompatible(currentUomId, targetBaseUomId, conversions);
        const nextUomId = isValid ? currentUomId : prod.uomId || null;
        form.setValue(`items.${index}.uomId`, nextUomId);
        form.setValue(
          `items.${index}.qty`,
          toBaseQty(
            conversions,
            purchaseQty,
            nextUomId || "",
            targetBaseUomId,
            true,
          ),
        );
        if (!priceVal && prod.cogs) {
          form.setValue(`items.${index}.pricePerUnit`, Number(prod.cogs));
        }
      }
    }
  };

  return (
    <div
      className={`flex flex-col gap-2 rounded-lg border p-2.5 transition-colors ${
        isStockReceipt
          ? "bg-emerald-50/40 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-800"
          : "bg-muted/20"
      }`}
    >
      {itemType && (
        <ExpenseItemModeBar
          itemType={itemType}
          canRemove={canRemove}
          onSwitchType={switchType}
          onRemove={onRemove}
        />
      )}

      {/* Input Fields Row */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2">
        {/* Item / Description Picker */}
        <div className="flex-1">
          {isStockReceipt ? (
            <div className="flex gap-2">
              <div className="flex-1">
                <SearchableSelect
                  value={itemId || ""}
                  onChange={pickItem}
                  placeholder={
                    itemType === "raw_material"
                      ? "Cari bahan baku..."
                      : "Cari produk..."
                  }
                  options={
                    itemType === "raw_material"
                      ? rawMaterials
                          .filter((rm) => rm.isActive || rm.id === itemId)
                          .map((rm) => ({
                            value: rm.id,
                            label: `${rm.name}${rm.sku ? ` (${rm.sku})` : ""}${!rm.isActive ? " (Nonaktif)" : ""}`,
                            hint: `Satuan: ${uomMap.get(rm.uomId)?.name || "Unit"}`,
                          }))
                      : products.map((p) => ({
                          value: p.id,
                          label: `${p.name}${p.sku ? ` (${p.sku})` : ""}`,
                        }))
                  }
                  className="h-9 text-xs"
                />
              </div>

              {/* UoM Dropdown */}
              <div className="w-28 shrink-0">
                <SearchableSelect
                  value={activeUomId}
                  onChange={(val) => {
                    form.setValue(`items.${index}.uomId`, val || null);
                    pinPurchaseQty();
                    // Re-sync base qty to the new unit
                    form.setValue(
                      `items.${index}.qty`,
                      toBaseQty(
                        conversions,
                        purchaseQty,
                        val || baseUomId,
                        baseUomId,
                        isStockReceipt,
                      ),
                    );
                  }}
                  placeholder="Satuan"
                  options={filterUomOptions({
                    uoms,
                    baseUomId,
                    activeUomId,
                    conversions,
                  }).map((u) => ({
                    value: u.id,
                    label: `${u.name}${u.symbol ? ` (${u.symbol})` : ""}${!u.isActive ? " (Nonaktif)" : ""}`,
                  }))}
                  className="h-9 text-xs"
                />
              </div>
            </div>
          ) : (
            <FormField
              control={form.control}
              name={`items.${index}.description`}
              render={({ field: f }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <Input
                      placeholder="Nama barang / jasa / operasional"
                      className="h-9 text-base sm:text-xs"
                      {...f}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>

        {/* Qty & Price & Subtotal */}
        <div className="flex items-center gap-2">
          <FormField
            control={form.control}
            name={
              isStockReceipt ? `items.${index}.qtyInUom` : `items.${index}.qty`
            }
            render={({ field: f }) => (
              <FormItem className="w-20 sm:w-20">
                <span className="text-[10px] text-muted-foreground sm:hidden">
                  Qty {isStockReceipt ? `(${activeUomName})` : ""}
                </span>
                <FormControl>
                  <NumberInput
                    placeholder="Qty"
                    className="h-9 text-base text-center sm:text-xs"
                    value={f.value ?? 1}
                    onChange={(value) => {
                      const val = value || 1;
                      f.onChange(val);
                      // Sync base qty
                      form.setValue(
                        `items.${index}.qty`,
                        toBaseQty(
                          conversions,
                          val,
                          activeUomId,
                          baseUomId,
                          isStockReceipt,
                        ),
                      );
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={`items.${index}.pricePerUnit`}
            render={({ field: f }) => (
              <FormItem className="flex-1 sm:w-28 sm:flex-initial">
                <span className="text-[10px] text-muted-foreground sm:hidden">
                  Harga / {isStockReceipt ? activeUomName : "Unit"}
                </span>
                <FormControl>
                  <NumberInput
                    placeholder="Harga"
                    className="h-9 text-base text-right sm:text-xs"
                    value={f.value ?? 0}
                    onChange={f.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <div className="w-24 shrink-0 text-right text-xs font-semibold text-foreground truncate">
            Rp {lineSubtotal.toLocaleString("id-ID")}
          </div>

          {!itemType && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
              disabled={!canRemove}
              onClick={onRemove}
              title="Hapus baris"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Conversion helper notification */}
      {conversionText && (
        <div className="flex items-center gap-1.5 text-[11px] text-emerald-700 dark:text-emerald-400 bg-emerald-100/60 dark:bg-emerald-950/40 px-2 py-1 rounded">
          <ArrowRight className="h-3 w-3 shrink-0" />
          <span>{conversionText}</span>
        </div>
      )}
    </div>
  );
}
